/**
 * PhaseManager - Zentrale Komponente zur Phasen-Steuerung
 *
 * Rendert die aktuelle Phase-Komponente basierend auf dem
 * Spielzustand und Feature-Flags.
 */

import React, { Suspense, lazy, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { usePhase } from '../state/PhaseContext';
import { useFeature } from '../features/FeatureProvider';
import { useGameState } from '../state';
import { PHASES } from './PhaseRegistry';
// eslint-disable-next-line no-unused-vars
import { PhaseSplash } from '../components/playing/PhaseSplash';
import { MinigameOverlay } from '../components/minigame';
import { determineCurrentPhase } from '../messages';

// === Lazy-loaded Phase Components ===
// Diese werden erst geladen wenn sie benötigt werden

const phaseComponents = {
  // Meta-Phasen
  [PHASES.INTRO]: lazy(() => import('./intro/IntroPhase')),
  [PHASES.PROFILE_SETUP]: lazy(() => import('./profileSetup/ProfileSetupPhase')),
  [PHASES.START_SCREEN]: lazy(() => import('./startScreen/StartScreenPhase')),
  [PHASES.STAMMTISCH_SETUP]: lazy(() => import('./stammtischSetup/StammtischSetupPhase')),
  [PHASES.STAMMTISCH]: lazy(() => import('./stammtisch/StammtischPhase')),
  [PHASES.GAME_MODE_SELECT]: lazy(() => import('./gameMode/GameModePhase')),
  [PHASES.QUICK_PLAY]: lazy(() => import('./quickPlay/QuickPlayPhase')),
  [PHASES.LOBBY]: lazy(() => import('./lobby/LobbyPhase')),

  // Pre-Game
  [PHASES.PRE_GAME_SHOP]: lazy(() => import('./preGameShop/PreGameShopPhase')),

  // Hauptspiel
  [PHASES.PLAYING]: lazy(() => import('./playing/PlayingPhase')),
  [PHASES.ROUND_END]: lazy(() => import('./roundEnd/RoundEndPhase')),
  // victorySplash: Intermediate phase before victory - keep PlayingPhase rendered
  victorySplash: lazy(() => import('./playing/PlayingPhase')),
  // @deprecated - Exchange ist jetzt Sub-Phase von PlayingPhase
  // Server sendet 'playing' mit exchangeState statt 'exchanging'
  // Fallback auf PlayingPhase falls alte Phase noch verwendet wird
  [PHASES.EXCHANGE]: lazy(() => import('./playing/PlayingPhase')),
  [PHASES.SHOP]: lazy(() => import('./shop/ShopPhase')),

  // Victory - Now handled by RoundEndPhase with VICTORY_SLIDES
  // RoundEndPhase checks isGameComplete and shows appropriate slides
  [PHASES.VICTORY]: lazy(() => import('./roundEnd/RoundEndPhase')),
  [PHASES.VICTORY_OVERVIEW]: lazy(() => import('./roundEnd/RoundEndPhase')),
  [PHASES.VICTORY_STATISTICS]: lazy(() => import('./roundEnd/RoundEndPhase')),
  [PHASES.VICTORY_AWARDS]: lazy(() => import('./roundEnd/RoundEndPhase')),
  [PHASES.VICTORY_FINAL]: lazy(() => import('./roundEnd/RoundEndPhase')),

  // Sonderzustände
  [PHASES.DISCONNECTED]: lazy(() => import('./special/DisconnectedPhase')),
  [PHASES.ERROR]: lazy(() => import('./special/ErrorPhase')),
};

/**
 * Fallback-Komponente für fehlende Phasen
 */
function PhasePlaceholder({ phase }) {
  return (
    <div className="phase-placeholder">
      <h2>Phase: {phase}</h2>
      <p>Diese Phase ist noch nicht implementiert.</p>
    </div>
  );
}

/**
 * Loading-Komponente während Phase lädt
 */
function PhaseLoadingScreen() {
  return (
    <div className="phase-loading">
      <h1 className="phase-loading-logo">Randalaise</h1>
      <div className="phase-loading-spinner" />
    </div>
  );
}

/**
 * Error Boundary für Phase-Komponenten
 */
class PhaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Phase Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="phase-error">
          <h2>Fehler in Phase</h2>
          <p>{this.state.error?.message || 'Unbekannter Fehler'}</p>
          <button onClick={() => this.setState({ hasError: false })}>
            Erneut versuchen
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * PhaseManager Component
 */
export function PhaseManager() {
  const { currentPhase, phaseMeta, isVictoryPhase } = usePhase();
  const gc = useGameState();

  // === Phase Splash State ===
  const [showPhaseSplash, setShowPhaseSplash] = useState(false);
  const [splashPhase, setSplashPhase] = useState(null);
  const prevPhaseRef = useRef(null);

  // Determine display phase for splash
  const displayPhase = useMemo(() => {
    // For playing phase, check sub-phases
    if (currentPhase === PHASES.PLAYING && gc) {
      return determineCurrentPhase(gc);
    }
    // Map main phases to splash phases
    if (currentPhase === PHASES.SHOP) return 'shop';
    if (currentPhase === PHASES.PRE_GAME_SHOP) return 'shop';
    return null; // Other phases don't get splash
  }, [currentPhase, gc]);

  // Detect phase changes and show splash
  useEffect(() => {
    if (displayPhase && displayPhase !== prevPhaseRef.current) {
      const previousPhase = prevPhaseRef.current;

      // Don't show splash for roundEnd (has its own overlay) or gameEnd
      if (displayPhase !== 'roundEnd' && displayPhase !== 'gameEnd') {
        // If coming from cardCount, delay splash to let dealing animation finish
        // Animation: cardCount * 60ms stagger + 600ms flight
        // Fade in: 800ms
        // Wait: 1000ms
        const comingFromCardCount = previousPhase === 'cardCount';
        const dealingAnimationDelay = comingFromCardCount
          ? (gc?.cardsPerPlayer || 13) * 20 + 2400
          : 0;

        if (dealingAnimationDelay > 0) {
          const timer = setTimeout(() => {
            setSplashPhase(displayPhase);
            setShowPhaseSplash(true);
          }, dealingAnimationDelay);
          prevPhaseRef.current = displayPhase;
          return () => clearTimeout(timer);
        } else {
          setSplashPhase(displayPhase);
          setShowPhaseSplash(true);
        }
      }
    }
    prevPhaseRef.current = displayPhase;
  }, [displayPhase, gc?.cardsPerPlayer]);

  const handlePhaseSplashComplete = useCallback(() => {
    setShowPhaseSplash(false);
  }, []);

  // Feature-Flags für Phase prüfen
  const featureEnabled = useFeature(phaseMeta.featureFlag || 'ALWAYS_ON');

  // Phase-Komponente ermitteln
  const PhaseComponent = useMemo(() => {
    // Prüfen ob Feature aktiv
    if (phaseMeta.featureFlag && !featureEnabled) {
      console.log(`Phase ${currentPhase} disabled by feature flag`);
      return null;
    }

    // Komponente aus Registry holen
    const Component = phaseComponents[currentPhase];

    if (!Component) {
      console.warn(`No component registered for phase: ${currentPhase}`);
      return () => <PhasePlaceholder phase={currentPhase} />;
    }

    return Component;
  }, [currentPhase, phaseMeta.featureFlag, featureEnabled]);

  // Keine Komponente -> nichts rendern
  if (!PhaseComponent) {
    return null;
  }

  // Victory-Phasen bekommen die Subphase als Prop
  const phaseProps = isVictoryPhase
    ? { subphase: currentPhase }
    : {};

  return (
    <>
      {/* Phase Splash */}
      {showPhaseSplash && (
        <PhaseSplash
          phase={splashPhase}
          partieNumber={splashPhase === 'shop' ? (gc?.roundNumber || 0) + 1 : (gc?.roundNumber || 1)}
          onComplete={handlePhaseSplashComplete}
        />
      )}

      <PhaseErrorBoundary key={PhaseComponent}>
        <Suspense fallback={<PhaseLoadingScreen />}>
          <div
            className={`phase-container phase-${currentPhase}`}
            data-phase={currentPhase}
          >
            <PhaseComponent {...phaseProps} />
          </div>
        </Suspense>
      </PhaseErrorBoundary>

      {/* Minigame Overlay */}
      <MinigameOverlay />
    </>
  );
}

/**
 * PhaseHeader - Optionaler Header für Phasen
 */
export function PhaseHeader() {
  const { phaseMeta } = usePhase();

  if (!phaseMeta.showHeader) {
    return null;
  }

  return (
    <div className="phase-header">
      <h1>{phaseMeta.name}</h1>
      {phaseMeta.description && (
        <p className="phase-description">{phaseMeta.description}</p>
      )}
    </div>
  );
}

/**
 * Hook zum Registrieren einer neuen Phase-Komponente
 * (für dynamische Phase-Registrierung)
 */
export function useRegisterPhase(phaseId, component) {
  useMemo(() => {
    if (phaseId && component) {
      phaseComponents[phaseId] = component;
    }
  }, [phaseId, component]);
}

export default PhaseManager;
